import { useEffect, useRef } from "react";

import useAuthStore from "@/store/auth";
import { useLocation, useNavigate } from "@tanstack/react-router";

import axios, { axiosPrivate } from "../api/axios";

axiosPrivate.interceptors.request.use((config) => {
	const accessToken = useAuthStore.getState().tokens?.accessToken;
	if (accessToken && !config.headers.Authorization && !config.headers.authorization) {
		config.headers.Authorization = `Bearer ${accessToken}`;
	}
	return config;
});

export function useRefreshToken() {
	const user = useAuthStore(state => state.user);
	const setTokens = useAuthStore(state => state.setTokens);
	const refreshToken = useAuthStore(state => state.tokens?.refreshToken);

	const refresh = async (): Promise<{
		access_token: string;
		refresh_token: string;
	}> => {
		if (!user)
			throw new Error("User cannot be null");
		try {
			const response = await axios.post(
				"auth/refresh_token",
				{
					userId: user.id,
					oldRefreshToken: refreshToken
				},
				{
					headers: {
						"Content-Type": "application/json"
					}
				}
			);
			const { access_token, refreshToken: refresh_token } = response.data.data;
			setTokens({
				accessToken: access_token,
				refreshToken: refresh_token
			});
			return { access_token, refresh_token };
		}
		catch (error) {
			// @ts-expect-error error type is unknown
			if (error.response?.data?.message === "Token is already invalid") {
				throw new Error("Token is already invalid");
			}
			console.error("Error refreshing token:", error);
			throw error;
		}
	};
	return refresh;
}

export function usePrivateAxios() {
	const refresh = useRefreshToken();
	const refreshFailCountRef = useRef(0);
	const tokens = useAuthStore(state => state.tokens);
	const setTokens = useAuthStore(state => state.setTokens);
	const logout = useAuthStore(state => state.logout);

	const navigate = useNavigate();

	const pathname = useLocation({
		select: location => location.pathname
	});

	useEffect(() => {
		refreshFailCountRef.current = 0;
		const requestIntercept = axiosPrivate.interceptors.request.use(
			(config) => {
				const accessToken = useAuthStore.getState().tokens?.accessToken ?? tokens?.accessToken;
				const hasHeader
                    = config.headers.Authorization
                    || config.headers.authorization;
				if (!hasHeader && accessToken) {
					config.headers.Authorization = `Bearer ${accessToken}`;
					config.headers.authorization = `Bearer ${accessToken}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const responseIntercept = axiosPrivate.interceptors.response.use(
			(response) => {
				return response;
			},

			async (error) => {
				const prevRequest = error?.config;
				if (
					error?.response?.status === 403
					|| (error?.response?.status === 401 && !prevRequest?.sent)
				) {
					prevRequest.sent = true;

					try {
						const { access_token, refresh_token } = await refresh();
						console.log(access_token, refresh_token);
						refreshFailCountRef.current = 0;
						if (access_token && refresh_token) {
							setTokens({
								accessToken: access_token,
								refreshToken: refresh_token
							});
						}
						prevRequest.headers.Authorization = `Bearer ${access_token}`;
						prevRequest.headers.authorization = `Bearer ${access_token}`;
						return axiosPrivate(prevRequest);
					}
					catch (refreshError) {
						refreshFailCountRef.current++;

						if (refreshFailCountRef.current >= 3) {
							if (pathname !== "/sign-in" && pathname !== "/verify-email") {
								logout();
								return navigate({ to: "/sign-in" });
							}
						}

						return Promise.reject(refreshError);
					}
				}
				return Promise.reject(error);
			}
		);

		return () => {
			axiosPrivate.interceptors.request.eject(requestIntercept);
			axiosPrivate.interceptors.response.eject(responseIntercept);
		};
	}, [tokens?.accessToken]);

	return axiosPrivate;
}
